
import React from 'react';
import { Check } from 'lucide-react';

const PricingSection = ({
  onPlanSelect,
}: {
  onPlanSelect?: (
    plan: 'Starter' | 'Professional' | 'Enterprise',
    message: string
  ) => void;
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
      {/* Tier 1: Starter */}
      <div className="relative h-full">
        <div className="absolute inset-0 bg-zinc-900 rounded-xl border border-zinc-800 hover:border-violet-400/50 transition-all duration-300"></div>
        <div className="relative z-10 p-8 h-full flex flex-col">
          <div className="flex-grow-0">
            <div className="h-10 flex items-center gap-3 mb-4">
              <div className="p-2 bg-zinc-800 rounded-lg">
                <div className="w-8 h-8 flex items-center justify-center">S</div>
              </div>
              <h3 className="text-xl font-semibold text-zinc-100">Starter</h3>
            </div>
            <div className="mb-6">
              <span className="text-4xl font-bold text-zinc-100">$1,500</span>
              <span className="text-zinc-500">/month</span>
            </div>
            <button
              className="w-full py-3 px-4 rounded-xl bg-zinc-800 text-zinc-100 hover:bg-[#c394ff]/20 transition-all duration-300 mb-6"
              onClick={() =>
                onPlanSelect?.(
                  'Starter',
                  "Hi! I’m interested in the Starter plan to kick off a small project and validate approach. Could we discuss scope, timeline, and next steps to get started this month?"
                )
              }
            >
              Get Started
            </button>
          </div>
          <div className="flex-grow space-y-4 pt-6">
            {[
              "20 hours of development",
              "Standard email & chat support",
              "Monthly maintenance & updates",
              "Basic feature implementation",
              "Weekly progress updates",
              "Source code access"
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-violet-400" />
                <span className="text-zinc-400">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tier 2: Professional */}
      <div className="relative h-full">
        <div className="absolute inset-0 bg-violet-600 rounded-xl"></div>
        <div className="relative z-10 p-8 h-full flex flex-col">
          <div className="flex-grow-0">
            <div className="h-10 flex items-center gap-3 mb-4 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-zinc-100 text-zinc-900 px-3 py-0.5 rounded-full text-xs font-medium whitespace-nowrap">
                Most Popular
              </div>
              <div className="p-2 bg-violet-500 rounded-lg">
                <div className="w-8 h-8 flex items-center justify-center">PRO</div>
              </div>
              <h3 className="text-xl font-semibold text-zinc-100">Professional</h3>
            </div>
            <div className="mb-6">
              <span className="text-4xl font-bold text-zinc-100">$3,200</span>
              <span className="text-violet-200">/month</span>
            </div>
            <button
              className="w-full py-3 px-4 rounded-xl bg-zinc-100 text-zinc-900 hover:bg-white transition-all duration-300 mb-6"
              onClick={() =>
                onPlanSelect?.(
                  'Professional',
                  "Hi there! I’m leaning toward the Professional plan for ongoing feature work and faster support. Can we review priorities, integration needs, and a kickoff schedule to begin soon?"
                )
              }
            >
              Choose Pro
            </button>
          </div>
          <div className="flex-grow space-y-4 pt-6">
            {[
              "50 hours of development",
              "Priority support (email, chat, phone)",
              "Custom feature development",
              "API integration",
              "Weekly progress reports",
              "Dedicated project manager"
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-violet-200" />
                <span className="text-violet-100">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tier 3: Enterprise */}
      <div className="relative h-full">
        <div className="absolute inset-0 bg-zinc-900 rounded-xl border border-zinc-800 hover:border-violet-400/50 transition-all duration-300"></div>
        <div className="relative z-10 p-8 h-full flex flex-col">
          <div className="flex-grow-0">
            <div className="h-10 flex items-center gap-3 mb-4">
              <div className="p-2 bg-zinc-800 rounded-lg">
                <div className="w-8 h-8 flex items-center justify-center">ENT</div>
              </div>
              <h3 className="text-xl font-semibold text-zinc-100">Enterprise</h3>
            </div>
            <div className="mb-6">
              <span className="text-4xl font-bold text-zinc-100">$6,500</span>
              <span className="text-zinc-500">/month</span>
            </div>
            <button
              className="w-full py-3 px-4 rounded-xl bg-zinc-800 text-zinc-100 hover:bg-[#c394ff]/20 transition-all duration-300 mb-6"
              onClick={() =>
                onPlanSelect?.(
                  'Enterprise',
                  "Hello! I’m exploring the Enterprise plan for larger initiatives, deeper integrations, and dedicated support. I’d love to discuss goals, security requirements, and resourcing to align on a roadmap and timeline."
                )
              }
            >
              Contact Us
            </button>
          </div>
          <div className="flex-grow space-y-4 pt-6">
            {[
              "100+ hours of development",
              "Dedicated development team",
              "Full-cycle application development",
              "Advanced integrations & custom APIs",
              "On-demand strategy sessions",
              "24/7 critical support"
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-violet-400" />
                <span className="text-zinc-400">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingSection;

